import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Download,
  FileText,
  Image as ImageIcon,
  Film,
  ZoomIn,
} from "lucide-react";
import { toastApiError } from "@/lib/errorMessages";
import { Button, Modal, ModalFooter, TruncatedText } from "@/components/ui";
import { ItemThumbnail } from "@/components/common";
import { cn } from "@/lib/utils";
import { downloadStorageUrl } from "@/lib/storageFetch";
import { formatFileSize } from "@/components/tickets/config";

export interface ChatMessageAttachment {
  id: string;
  file_name: string;
  file_url: string;
  file_type: "image" | "video" | "document";
  file_size: number;
}

function AttachmentImagePreview({
  fileUrl,
  fileName,
}: {
  fileUrl: string;
  fileName: string;
}) {
  const { t } = useTranslation("components");
  const { t: tCommon } = useTranslation("common");
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleDownload = async () => {
    try {
      await downloadStorageUrl(fileUrl, fileName);
    } catch (err) {
      toastApiError(err, "generic", t("attachmentDownloadFailed"));
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setPreviewOpen(true)}
        className="group relative block overflow-hidden rounded-xl shadow-sm ring-1 ring-surface-200 transition-all hover:shadow-lg hover:ring-primary-300"
        aria-label={t("attachmentViewFull")}
      >
        <ItemThumbnail
          imageUrl={fileUrl}
          alt={fileName}
          fill
          rounded="xl"
          className="h-32 w-32 max-h-48 max-w-full"
          imgClassName="transition-transform group-hover:scale-105"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
          <ZoomIn className="h-6 w-6 text-white opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
      </button>

      <Modal
        closeLabel={tCommon("close")}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={fileName}
        size="xl"
        showCloseButton
        footer={
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={() => void handleDownload()}
            >
              {t("attachmentDownload")}
            </Button>
          </ModalFooter>
        }
      >
        <ItemThumbnail
          imageUrl={fileUrl}
          alt={fileName}
          fill
          rounded="none"
          objectFit="contain"
          className="mx-auto h-[min(70vh,40rem)] w-full"
        />
      </Modal>
    </>
  );
}

interface ChatMessageAttachmentsProps {
  attachments: ChatMessageAttachment[];
  align?: "start" | "end";
}

export function ChatMessageAttachments({
  attachments,
  align = "start",
}: ChatMessageAttachmentsProps) {
  const { t } = useTranslation("components");

  if (attachments.length === 0) return null;

  return (
    <div
      className={cn(
        "mt-1.5 flex flex-wrap gap-2",
        align === "end" ? "justify-end" : "justify-start",
      )}
    >
      {attachments.map((attachment) => {
        const FileIcon =
          attachment.file_type === "image"
            ? ImageIcon
            : attachment.file_type === "video"
              ? Film
              : FileText;

        if (attachment.file_type === "image") {
          return (
            <AttachmentImagePreview
              key={attachment.id}
              fileUrl={attachment.file_url}
              fileName={attachment.file_name}
            />
          );
        }

        return (
          <button
            key={attachment.id}
            type="button"
            onClick={async () => {
              try {
                await downloadStorageUrl(
                  attachment.file_url,
                  attachment.file_name,
                );
              } catch (err) {
                toastApiError(err, "generic", t("attachmentDownloadFailed"));
              }
            }}
            className="flex max-w-full items-center gap-3 rounded-lg bg-surface-0 px-3 py-2 shadow-xs ring-1 ring-surface-200 transition-colors hover:bg-surface-50"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-100">
              <FileIcon className="h-4 w-4 text-surface-500" />
            </div>
            <div className="min-w-0 text-left">
              <TruncatedText as="p" className="text-sm font-medium text-surface-800">
                {attachment.file_name}
              </TruncatedText>
              <p className="text-xs text-surface-400">
                {formatFileSize(attachment.file_size)}
              </p>
            </div>
            <Download className="h-4 w-4 shrink-0 text-surface-400" />
          </button>
        );
      })}
    </div>
  );
}
